import React from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import colors from "../../../assets/theme/colors";
import { getProfileSectionLabel } from "./profileFormHelpers";

const ProfileSectionSaveFooter = ({
  currentStep,
  sectionLabel = "",
  disabled = false,
  hasUnsavedChanges = true,
  showStatus = false,
  saveStatus = "idle",
  warning = "",
  retryingWarning = false,
  onSave,
  onRetryWarning,
}) => {
  const label = sectionLabel || getProfileSectionLabel(currentStep) || "Section";
  const isSaving = saveStatus === "saving";
  const isSaved = saveStatus === "saved";
  const isFailed = saveStatus === "failed";
  const statusLabel = isSaving
    ? "Saving…"
    : isSaved
      ? "Saved"
      : isFailed
        ? "Failed"
        : hasUnsavedChanges
          ? "Unsaved changes"
          : "All changes saved";
  const buttonLabel = isSaving
    ? "Saving…"
    : isSaved
      ? "Saved"
      : isFailed
        ? "Try again"
        : showStatus
          ? "Save"
          : `Save ${label}`;

  return (
    <Stack spacing={warning ? 0.8 : 0}>
      <Stack
        direction={showStatus ? { xs: "column", sm: "row" } : "column"}
        spacing={showStatus ? 0.9 : 0}
        alignItems={showStatus ? { xs: "stretch", sm: "center" } : "stretch"}
        justifyContent="space-between"
      >
        {showStatus ? (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850, color: colors.semantic.ink, lineHeight: 1.15, fontSize: "0.9rem" }}>
              {statusLabel}
            </Typography>
            <Typography sx={{ mt: 0.15, color: colors.semantic.ink2, lineHeight: 1.25, fontSize: "0.78rem" }}>
              Saving updates the whole care circle.
            </Typography>
          </Box>
        ) : null}
        <Button
          variant="contained"
          onClick={onSave}
          disabled={disabled || isSaving || isSaved}
          fullWidth={!showStatus}
          size="large"
          sx={{
            alignSelf: showStatus ? { xs: "stretch", sm: "center" } : "stretch",
            minWidth: showStatus ? { xs: "100%", sm: 150 } : "auto",
            py: { xs: 1, sm: 1.1 },
            px: showStatus ? 2.3 : undefined,
            textTransform: "none",
            borderRadius: 999,
            bgcolor: colors.semantic.profileFlow.completionText,
            "&:hover": { bgcolor: colors.brand.navy },
            minHeight: 48,
            fontWeight: 800,
            transition: "transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
            "&:active": {
              transform: "scale(0.98)",
            },
            "&.Mui-disabled": {
              opacity: 1,
              animation: "none",
              boxShadow: "none",
            },
          }}
        >
          {isSaving ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}
          {buttonLabel}
        </Button>
      </Stack>
      {warning ? (
        <Box role="status" sx={{ color: colors.semantic.warningText || colors.semantic.ink2 }}>
          <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.35 }}>
            {warning}
          </Typography>
          <Button
            size="small"
            onClick={onRetryWarning}
            disabled={retryingWarning}
            sx={{ mt: 0.25, p: 0, minWidth: 0, textTransform: "none", fontWeight: 800 }}
          >
            {retryingWarning ? "Retrying…" : "Try again"}
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
};

export default ProfileSectionSaveFooter;
